/**
 * BNPL Handler - Manages the Buy Now Pay Later modal and calculations
 */

(function() {
  'use strict';

  const BNPLHandler = {
    modal: null,
    overlay: null,
    currentPrice: 0,

    init() {
      this.modal = document.getElementById('bnpl-modal');
      if (!this.modal) return;

      this.overlay = this.modal.querySelector('.bnpl-modal-overlay');
      this.setupEventListeners();
      this.updatePrices();
    },

    setupEventListeners() {
      // Open modal when clicking BNPL section
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-bnpl-trigger]')) {
          e.preventDefault();
          this.openModal();
        }
      });

      // Close modal handlers
      const closeElements = this.modal.querySelectorAll('[data-bnpl-close]');
      closeElements.forEach(element => {
        element.addEventListener('click', () => this.closeModal());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.closeModal();
        }
      });

      // Update prices when variant changes
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          this.updatePrices(e.detail.variant.price);
        }
      });

      // Also listen for custom product price updates
      document.addEventListener('price:update', (e) => {
        if (e.detail && e.detail.price) {
          this.updatePrices(e.detail.price);
        }
      });
    },

    openModal() {
      if (!this.modal) return;

      this.modal.classList.add('bnpl-modal--open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus management
      const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Update prices when opening
      this.updatePricesFromPage();
    },

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('bnpl-modal--open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Return focus to trigger element
      const trigger = document.querySelector('[data-bnpl-trigger]');
      if (trigger) {
        trigger.focus();
      }
    },

    isOpen() {
      return this.modal && this.modal.classList.contains('bnpl-modal--open');
    },

    updatePricesFromPage() {
      // Try to get price from various sources
      let priceInCents = 0;

      // Check for variant price in product data
      const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"]');
      if (variantInput) {
        const priceElement = document.querySelector(`[data-variant-price="${variantInput.value}"]`);
        if (priceElement) {
          priceInCents = parseInt(priceElement.dataset.price || 0);
        }
      }

      // Fallback to current variant price element
      if (!priceInCents) {
        const currentPriceElement = document.querySelector('[data-product-price], .product-price, .price');
        if (currentPriceElement) {
          const priceText = currentPriceElement.textContent || currentPriceElement.innerText;
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            const price = parseFloat(priceMatch[0].replace(',', ''));
            priceInCents = Math.round(price * 100);
          }
        }
      }

      // Get price from product form if available
      if (!priceInCents && window.productPrice) {
        priceInCents = window.productPrice;
      }

      this.updatePrices(priceInCents);
    },

    updatePrices(priceInCents) {
      if (!priceInCents || priceInCents <= 0) {
        // Try to get from data attribute on BNPL trigger
        const trigger = document.querySelector('[data-bnpl-trigger]');
        if (trigger && trigger.dataset.price) {
          priceInCents = parseInt(trigger.dataset.price);
        }
      }

      if (!priceInCents || priceInCents <= 0) return;

      this.currentPrice = priceInCents;

      // Calculate installments
      const klarnaAmount = priceInCents / 3;
      const clearpayAmount = priceInCents / 4;
      const paypalAmount = priceInCents / 3;

      // Update Klarna prices
      this.updateElementPrice('[data-klarna-1]', klarnaAmount);
      this.updateElementPrice('[data-klarna-2]', klarnaAmount);
      this.updateElementPrice('[data-klarna-3]', klarnaAmount);

      // Update Clearpay prices
      this.updateElementPrice('[data-clearpay-1]', clearpayAmount);
      this.updateElementPrice('[data-clearpay-2]', clearpayAmount);
      this.updateElementPrice('[data-clearpay-3]', clearpayAmount);
      this.updateElementPrice('[data-clearpay-4]', clearpayAmount);

      // Update PayPal prices
      this.updateElementPrice('[data-paypal-1]', paypalAmount);
      this.updateElementPrice('[data-paypal-2]', paypalAmount);
      this.updateElementPrice('[data-paypal-3]', paypalAmount);
    },

    updateElementPrice(selector, amountInCents) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.textContent = this.formatPrice(amountInCents);
      });
    },

    formatPrice(cents) {
      // Use Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat || window.Shopify.money_format);
      }

      // Fallback: try to detect currency format from the page
      const priceElement = document.querySelector('.price__regular .price-item, .product-price, .price, [data-price]');
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        // Extract currency symbol or code from existing price
        const currencyMatch = priceText.match(/^([^\d\s,.-]+)/);
        if (currencyMatch) {
          const currency = currencyMatch[1].trim();
          const amount = (cents / 100).toFixed(2);
          return `${currency}${amount}`;
        }
      }

      // Last resort: format with current active currency if available
      const amount = (cents / 100).toFixed(2);
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return `${window.Shopify.currency.active} ${amount}`;
      }

      // Default fallback
      return amount.toString();
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BNPLHandler.init());
  } else {
    BNPLHandler.init();
  }

  // Expose to global scope for external access
  window.BNPLHandler = BNPLHandler;
})();